import React, { forwardRef, useEffect, useMemo, useState } from 'react';

import { Box, Divider, Flex, Grid, GridItem, Spinner } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { Card } from '@mesh.ai/rabbit';
import { useGetTimezoneOffsetEmployee } from 'components/hooks/useGetTimezoneOffsetEmployee';
import { useToast } from 'components/hooks/useToast';
import { format, formatISO, getUnixTime, isToday } from 'date-fns';
import { RRule } from 'rrule';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';

import { Calendar, Clock } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';
import { sendClickedOnEditOneOnOneEvent, sendRescheduledOneonone } from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';
import { DatePicker } from 'uiToolkitV1/DatePicker';
import { CustomDatePickerInputProps } from 'uiToolkitV1/DatePicker/DatePicker';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TextBodyLarge, TextLabel } from 'uiToolkitV1/Typography';

import { CustomRecurrenceModal, getWhatDayOfTheMonth } from '../../common/modals/CustomRecurrenceModal';
import { RecurringEventModal } from '../../common/modals/RecurringEventModal';
import { useOneOnOneInstanceDetails, useUpdateInstanceDetailsMutation } from '../../hooks/dataHooks';
import { INVALID_DATE_ERROR } from '../../services';
import { dayCapitalizedRRuleString } from '../../utils';

// @ts-ignore
export const TimeCustomInput = forwardRef(
  ({ value, onClick, showTimeSelectOnly, className }: CustomDatePickerInputProps, ref) => {
    const selectedDate = value ? new Date(value) : new Date();
    const formattedDate = showTimeSelectOnly ? value : format(selectedDate, 'MMM dd, yyyy');
    return (
      <Button
        StartIcon={Clock}
        variant="tertiary"
        style={{ paddingLeft: 0, borderBottom: '1px dashed #999999' }}
        onClick={onClick}
        ref={ref}
        sx={{
          p: {
            color: '#333333 !important',
            fontWeight: 400,
          },
          svg: {
            color: '#666666 !important',
            height: '18px',
            width: '18px',
          },
        }}
        className={`mesh-time-picker-button ${className}`}
      >
        {formattedDate}
      </Button>
    );
  }
);
interface Props {
  onSave?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isSaveDisabled?: boolean;
  source?: string;
}

const Container = styled(Card)`
  width: 100%;
  padding: 24px !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
`;

const Footer = ({ onCancel, onSave, isSaveDisabled, isLoading = false }: Props) => (
  <PaperWithShadow
    p="16px"
    w={'100%'}
    sx={{
      borderTopLeftRadius: '0px',
      borderTopRightRadius: '0px',
    }}
    background="#FDFDFD"
  >
    <Flex justifyContent="space-between">
      <Button
        sx={{
          '&>p': {
            marginLeft: 0,
          },
        }}
        variant="tertiary"
        onClick={onCancel}
        mr={!isMobileView() && 'auto'}
      >
        Cancel
      </Button>
      <Button
        w="62px"
        isDisabled={isSaveDisabled || isLoading}
        onClick={onSave}
        data-testid="meeting-details-save-button"
      >
        {isLoading ? (
          <Flex alignItems="center">
            <Spinner size="md" color="white" />
          </Flex>
        ) : (
          'Save'
        )}
      </Button>
    </Flex>
  </PaperWithShadow>
);

const DURATION_OPTIONS = [
  {
    label: '30mins',
    value: 30,
  },
  {
    label: '45mins',
    value: 45,
  },
  {
    label: '60mins',
    value: 60,
  },
];

const numberToWords = {
  1: 'First',
  2: 'Second',
  3: 'Third',
  4: 'Fourth',
  5: 'Fifth',
};

const MeetingsDetailsExpanded = ({ onCancel, onSave, source }: Props) => {
  const { masterId, instanceId } = useOneOnOnePageParams();

  const toast = useToast();

  const employeeTimezone = useGetTimezoneOffsetEmployee();

  const { startTimeTs, recurrenceRule, duration } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const onSuccess = () => {
    toast({
      title: 'Your meeting schedule has been updated.',
      status: 'success',
    });
  };
  const onError = ({ err }: { err: any }) => {
    if (err?.name === INVALID_DATE_ERROR) {
      toast({
        title: err?.message,
        status: 'error',
      });
      return;
    }
    toast({
      title:
        "We're sorry, we were unable to update your meeting schedule. Please try again later or contact support for assistance.",
      status: 'error',
    });
  };

  const updateInstanceDetailsMutation = useUpdateInstanceDetailsMutation({
    masterId,
    instanceId,
    onSuccess,
    onError,
  });

  const startTime = new Date(startTimeTs * 1000);

  const [selectedDate, setSelectedDate] = useState(startTime);

  const [selectedFrequency, setSelectedFrequency] = useState(recurrenceRule);

  const [selectedDuration, setSelectedDuration] = useState(duration);

  const [isEditSingleMeeting, setIsEditSingleMeeting] = useState(true);

  useEffect(() => {
    setSelectedFrequency(recurrenceRule);
  }, [recurrenceRule]);

  const selectedDay = format(selectedDate, 'EEEE');

  const selectedDurationOption = DURATION_OPTIONS.find((option) => option.value === selectedDuration);

  const { nthDayOfTheMonth, isLastNthDayOfTheMonth } = getWhatDayOfTheMonth(selectedDate);

  const meetingFrequencies = useMemo(() => {
    const frequencies: Array<{
      label: string;
      value: string;
      id: 'weekly' | 'biweekly' | 'monthly' | 'monthly-last' | 'custom' | 'selectedFrequency';
    }> = [
      {
        label: `Weekly on ${selectedDay}`,
        value: new RRule({
          freq: RRule.WEEKLY,
          interval: 1,
          byweekday: [RRule[format(selectedDate, 'EEEEEE').toUpperCase()]],
        })
          .toString()
          .split('RRULE:')[1],
        id: 'weekly',
      },
      {
        label: `Biweekly on ${selectedDay}`,
        value: new RRule({
          freq: RRule.WEEKLY,
          interval: 2,
          byweekday: [RRule[format(selectedDate, 'EEEEEE').toUpperCase()]],
        })
          .toString()
          .split('RRULE:')[1],
        id: 'biweekly',
      },
      {
        label: `Monthly on the ${numberToWords[nthDayOfTheMonth]} ${format(selectedDate, 'EEEE')}`,
        value: new RRule({
          freq: RRule.MONTHLY,
          interval: 1,
          byweekday: [RRule[format(selectedDate, 'EEEEEE').toUpperCase()]?.nth(nthDayOfTheMonth)],
        })
          .toString()
          .split('RRULE:')[1],
        id: 'monthly',
      },
    ];
    // thanks to UAT feedback, code is messy now :(
    if (isLastNthDayOfTheMonth) {
      frequencies.push({
        label: `Monthly on the last ${format(selectedDate, 'EEEE')}`,
        value: new RRule({
          freq: RRule.MONTHLY,
          interval: 1,
          byweekday: [RRule[format(selectedDate, 'EEEEEE').toUpperCase()]?.nth(-1)],
        })
          .toString()
          .split('RRULE:')[1],
        id: 'monthly-last',
      });
    }
    const selectedFrequencyIsNotInFrequencies = !frequencies.some((frequency) => frequency.value === selectedFrequency);
    if (selectedFrequencyIsNotInFrequencies && selectedFrequency) {
      // capitalizing every word apart from "on" or "the" or "on the"
      const label = dayCapitalizedRRuleString(RRule.fromString(selectedFrequency).toText());
      frequencies.push({
        label,
        value: RRule.fromString(selectedFrequency).toString().split('RRULE:')[1],
        id: 'selectedFrequency',
      });
    }
    frequencies.push({
      label: 'Custom',
      value: 'custom',
      id: 'custom',
    });
    return frequencies;
  }, [selectedDate, isLastNthDayOfTheMonth, nthDayOfTheMonth, selectedDay, selectedFrequency]);

  const selectedFrequencyOption =
    meetingFrequencies.find((frequency) => frequency.value === selectedFrequency) ??
    meetingFrequencies?.find((frequency) => frequency.id === 'selectedFrequency');

  const frequencyOnChange = ({ value }: { value: string }) => {
    if (value === 'custom') {
      setIsCustomRecurrenceModalOpen(true);
      return;
    }
    setSelectedFrequency(value);
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  // modal states
  const [isCustomRecurrenceModalOpen, setIsCustomRecurrenceModalOpen] = useState(false);

  const onCustomRecurrenceSaveHandler = (recurrenceRule: string) => {
    setSelectedFrequency(recurrenceRule);
    setIsCustomRecurrenceModalOpen(false);
  };

  const [isRecurringEventModalOpen, setIsRecurringEventModalOpen] = useState(false);

  const hasAnyFieldChanged = () =>
    selectedDate.getTime() !== startTime.getTime() ||
    selectedFrequency !== recurrenceRule ||
    selectedDuration !== duration;

  const hasFrequencyChanged = () => selectedFrequency !== recurrenceRule;

  const updateInstanceDetailsHandler = async ({ editingSingleMeeting }: { editingSingleMeeting?: boolean }) => {
    // REFER: https://stackoverflow.com/a/54561254/8487836
    // const usersBrowserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startTimeUTC = getUnixTime(selectedDate);
    // console.log({ startTimeUTC: startTimeUTC.toISOString() });
    try {
      await updateInstanceDetailsMutation.mutateAsync({
        updatedMeetingDetails: {
          startTimeTs: startTimeUTC,
          isEditSingleMeeting: editingSingleMeeting ?? isEditSingleMeeting,
          startDate: formatISO(selectedDate).split('T')[0],
          recurrenceRule: selectedFrequency,
          duration: selectedDuration,
        },
      });
      onSave?.();
      sendClickedOnEditOneOnOneEvent({
        eventProperties: {
          instance_id: instanceId,
          Frequency: selectedFrequency,
          source,
        },
      });
      sendRescheduledOneonone({
        eventProperties: {
          type: (editingSingleMeeting ?? isEditSingleMeeting) ? 'single instance' : 'entire series',
        },
      });
    } catch (e) {
      console.error('Error while updating meeting details', e);
    } finally {
      onSave?.();
    }
  };

  return (
    <>
      <Flex w="100%" flexDir="column">
        <Container isMobileView={isMobileView()}>
          <Flex alignItems="center" mb="18px">
            <Calendar mr="12px" color="#333333" boxSize="24px" />
            <TextBodyLarge bold>Edit Meeting Details</TextBodyLarge>
          </Flex>
          <Divider opacity={1} color="#EBEFF3" />

          <Box mt="18px">
            <Grid templateColumns="repeat(3, 1fr)">
              <GridItem>
                <Flex
                  sx={{
                    '.mesh-date-picker-button': {
                      width: '90%',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      borderRadius: 0,
                    },
                    _hover: {
                      '.mesh-date-picker-button': {
                        borderBottom: '1px solid #333333 !important',
                      },
                      '.mesh-date-picker-button-open': {
                        borderBottom: '1px solid #1565bc !important',
                      },
                    },
                    '.mesh-date-picker-button-open': {
                      borderBottom: '1px solid #1565bc !important',
                    },
                  }}
                  flexDir="column"
                  alignItems="flex-start"
                >
                  <TextLabel color="text.g2">Meeting Date</TextLabel>
                  <DatePicker
                    onChange={(date) => handleDateChange(date)}
                    selectedDate={selectedDate}
                    minDate={new Date()}
                  />
                </Flex>
              </GridItem>
              <GridItem>
                <Flex
                  sx={{
                    '.mesh-time-picker-button': {
                      width: '90%',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      borderRadius: 0,
                    },
                    _hover: {
                      '.mesh-time-picker-button': {
                        borderBottom: '1px solid #333333 !important',
                      },
                      '.mesh-date-picker-button-open': {
                        borderBottom: '1px solid #1565bc !important',
                      },
                    },

                    '.mesh-date-picker-button-open': {
                      borderBottom: '1px solid #1565bc !important',
                    },
                  }}
                  flexDir="column"
                  alignItems="flex-start"
                >
                  <TextLabel color="text.g2">Meeting Time</TextLabel>
                  <DatePicker
                    onChange={(date) => handleDateChange(date)}
                    selectedDate={selectedDate}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption=""
                    dateFormat="h:mm aa"
                    CustomInput={TimeCustomInput}
                    // disable past time to be selected in Date type
                    minTime={selectedDate && isToday(selectedDate) ? new Date() : undefined}
                    maxTime={selectedDate && isToday(selectedDate) ? new Date(0, 0, 0, 23, 59) : undefined}
                    className="edit-one-on-one-meeting-time"
                  />
                  <TextLabel color="text.g3" mt="4px">
                    {employeeTimezone}
                  </TextLabel>
                </Flex>
              </GridItem>
              <GridItem>
                <Flex flexDir="column" alignItems="flex-start">
                  <TextLabel color="text.g2">Duration</TextLabel>
                  {/* @ts-ignore */}
                  <SelectDashed
                    minWidth={isMobileView() && '40px'}
                    options={DURATION_OPTIONS}
                    isSearchable={false}
                    value={selectedDurationOption}
                    handleChange={({ value }) => {
                      setSelectedDuration(value);
                    }}
                    className="edit-one-on-one-meeting-duration"
                  />
                </Flex>
              </GridItem>
            </Grid>
            <Flex
              sx={{
                '.select': {
                  width: '64%',
                  marginTop: '8px',
                  '.select__control': {
                    paddingBottom: '8px',
                  },
                  '& .select__single-value': {
                    fontWeight: 'normal', // Override the font weight for the selected value
                  },
                  _hover: {
                    '.select__control': {
                      borderBottom: '1px solid #333333 !important',
                    },
                    '.select__control--menu-is-open': {
                      borderBottom: '1px solid #1565bc !important',
                    },
                  },
                  '.select__control--menu-is-open': {
                    borderBottom: '1px solid #1565bc !important',
                  },
                },
              }}
              flexDir="column"
              alignItems="flex-start"
              mt="24px"
            >
              <TextLabel color="text.g2">Meeting Frequency</TextLabel>
              {/* @ts-ignore */}
              <SelectDashed
                options={meetingFrequencies}
                isSearchable={false}
                value={selectedFrequencyOption}
                handleChange={frequencyOnChange}
              />
            </Flex>
          </Box>
        </Container>
        <Footer
          onCancel={onCancel}
          onSave={() => {
            if (hasFrequencyChanged()) {
              updateInstanceDetailsHandler({ editingSingleMeeting: false });
              return;
            }
            setIsRecurringEventModalOpen(true);
          }}
          isSaveDisabled={!hasAnyFieldChanged()}
          isLoading={updateInstanceDetailsMutation?.isLoading}
        />
      </Flex>
      {isCustomRecurrenceModalOpen && (
        <CustomRecurrenceModal
          isOpen={isCustomRecurrenceModalOpen}
          onClose={() => {
            setIsCustomRecurrenceModalOpen(false);
          }}
          onSave={onCustomRecurrenceSaveHandler}
          selectedDate={selectedDate}
          recurrenceRule={selectedFrequency}
        />
      )}
      <RecurringEventModal
        isOpen={isRecurringEventModalOpen}
        onClose={() => {
          setIsRecurringEventModalOpen(false);
        }}
        onSave={updateInstanceDetailsHandler}
        isEditSingleMeeting={isEditSingleMeeting}
        onRadioChange={(value) => {
          setIsEditSingleMeeting(value);
        }}
      />
    </>
  );
};

export default MeetingsDetailsExpanded;
